import { css } from 'lit';

export default css`
  :host {
    display: block;
  }

  /** The popup */
  .select {
    flex: 1 1 auto;
    display: inline-flex;
    width: 100%;
    position: relative;
    vertical-align: middle;
  }

  .select::part(popup) {
    z-index: var(--webmodule-z-popup);
  }

  .select[data-current-placement^='top']::part(popup) {
    transform-origin: bottom;
  }

  .select[data-current-placement^='bottom']::part(popup) {
    transform-origin: top;
  }

  /* Combobox */
  .select__combobox {
    flex: 1;
    display: flex;
    width: 100%;
    min-width: 0;
    position: relative;
    align-items: center;
    justify-content: start;
    font-family: var(--webmodule-control-font-family);
    font-weight: var(--webmodule-control-font-weight);
    letter-spacing: var(--webmodule-control-letter-spacing);
    vertical-align: middle;
    overflow: hidden;
    cursor: pointer;
    transition:
      var(--webmodule-animate-fast) color,
      var(--webmodule-animate-fast) border,
      var(--webmodule-animate-fast) box-shadow,
      var(--webmodule-animate-fast) background-color;
  }

  /* Expanded-in-place overlay: opaque so disabled opacity does not show content underneath. */
  .select--expanded-overflow .select__combobox {
    overflow: visible;
    opacity: 1;
    box-shadow: var(--webmodule-shadow-medium);
  }

  .select--standard.select--expanded-overflow .select__combobox {
    background-color: var(--webmodule-control-background-color);
  }

  .select--standard.select--disabled.select--expanded-overflow .select__combobox {
    background-color: var(--webmodule-control-background-color-disabled);
    opacity: 1;
  }

  .select--filled.select--expanded-overflow .select__combobox {
    background-color: var(--webmodule-control-filled-background-color);
  }

  .select--filled.select--disabled.select--expanded-overflow .select__combobox {
    background-color: var(--webmodule-control-filled-background-color-disabled);
    opacity: 1;
  }

  /*
   * A disabled input swallows pointer events in Chrome and Firefox, which would stop the
   * hover ever reaching the control. pointer-events: none makes hit testing pass through
   * it to the combobox parent, which is where the expand-in-place listener lives. The
   * combobox already ignores interaction when disabled, so nothing is made clickable.
   */
  .select--disabled .select__display-input {
    pointer-events: none;
  }

  .select__display-input {
    position: relative;
    width: 100%;
    font: inherit;
    border: none;
    background: none;
    color: var(--webmodule-control-color);
    cursor: inherit;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 0;
    margin: 0;
    -webkit-appearance: none;
  }

  .select__display-input::placeholder {
    color: var(--webmodule-control-placeholder-color);
  }

  .select:not(.select--disabled):hover .select__display-input {
    color: var(--webmodule-control-color-hover);
  }

  .select__display-input:focus {
    outline: none;
  }

  /* Visually hide the display input when multiple is enabled */
  .select--multiple:not(.select--placeholder-visible) .select__display-input {
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
  }

  .select__value-input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    padding: 0;
    margin: 0;
    opacity: 0;
    z-index: -1;
  }

  .select__tags {
    display: flex;
    flex: 1;
    align-items: center;
    flex-wrap: wrap;
    margin-inline-start: var(--webmodule-spacing-2x-small);
  }

  .select__tags::slotted(webmodule-tag) {
    cursor: pointer !important;
  }

  .select--disabled .select__tags,
  .select--disabled .select__tags::slotted(webmodule-tag) {
    cursor: not-allowed !important;
  }

  /* Standard selects */
  .select--standard .select__combobox {
    background-color: var(--webmodule-control-background-color);
    border: solid var(--webmodule-control-border-width) var(--webmodule-control-border-color);
  }

  .select--standard.select--disabled .select__combobox {
    background-color: var(--webmodule-control-background-color-disabled);
    border-color: var(--webmodule-control-border-color-disabled);
    color: var(--webmodule-control-color-disabled);
    opacity: 0.5;
    cursor: not-allowed;
    outline: none;
  }

  .select--standard:not(.select--disabled).select--open .select__combobox,
  .select--standard:not(.select--disabled).select--focused .select__combobox {
    background-color: var(--webmodule-control-background-color-focus);
    border-color: var(--webmodule-control-border-color-focus);
    box-shadow: 0 0 0 var(--webmodule-focus-border-width) var(--webmodule-control-focus-ring-color);
  }

  /* Filled selects */
  .select--filled .select__combobox {
    border: none;
    background-color: var(--webmodule-control-filled-background-color);
    color: var(--webmodule-control-color);
  }

  .select--filled:hover:not(.select--disabled) .select__combobox {
    background-color: var(--webmodule-control-filled-background-color-hover);
  }

  .select--filled.select--disabled .select__combobox {
    background-color: var(--webmodule-control-filled-background-color-disabled);
    opacity: 0.5;
    cursor: not-allowed;
  }

  .select--filled:not(.select--disabled).select--open .select__combobox,
  .select--filled:not(.select--disabled).select--focused .select__combobox {
    background-color: var(--webmodule-control-filled-background-color-focus);
    outline: var(--webmodule-focus-border);
  }

  /* Sizes */
  .select--small .select__combobox {
    border-radius: var(--webmodule-control-border-radius-small);
    font-size: var(--webmodule-control-font-size-small);
    min-height: var(--webmodule-control-height-small);
    padding-block: 0;
    padding-inline: var(--webmodule-control-spacing-small);
  }

  .select--small .select__clear {
    margin-inline-start: var(--webmodule-control-spacing-small);
  }

  .select--small .select__prefix::slotted(*) {
    margin-inline-end: var(--webmodule-control-spacing-small);
  }

  .select--small.select--multiple .select__prefix::slotted(*) {
    margin-inline-start: var(--webmodule-control-spacing-small);
  }

  .select--small.select--multiple:not(.select--placeholder-visible) .select__combobox {
    padding-block: 2px;
    padding-inline-start: 0;
  }

  .select--small .select__tags {
    gap: 2px;
  }

  .select--medium .select__combobox {
    border-radius: var(--webmodule-control-border-radius-medium);
    font-size: var(--webmodule-control-font-size-medium);
    min-height: var(--webmodule-control-height-medium);
    padding-block: 0;
    padding-inline: var(--webmodule-control-spacing-medium);
  }

  .select--medium .select__clear {
    margin-inline-start: var(--webmodule-control-spacing-medium);
  }

  .select--medium .select__prefix::slotted(*) {
    margin-inline-end: var(--webmodule-control-spacing-medium);
  }

  .select--medium.select--multiple .select__prefix::slotted(*) {
    margin-inline-start: var(--webmodule-control-spacing-medium);
  }

  .select--medium.select--multiple .select__combobox {
    padding-inline-start: 0;
    padding-block: 3px;
  }

  .select--medium .select__tags {
    gap: 3px;
  }

  .select--large .select__combobox {
    border-radius: var(--webmodule-control-border-radius-large);
    font-size: var(--webmodule-control-font-size-large);
    min-height: var(--webmodule-control-height-large);
    padding-block: 0;
    padding-inline: var(--webmodule-control-spacing-large);
  }

  .select--large .select__clear {
    margin-inline-start: var(--webmodule-control-spacing-large);
  }

  .select--large .select__prefix::slotted(*) {
    margin-inline-end: var(--webmodule-control-spacing-large);
  }

  .select--large.select--multiple .select__prefix::slotted(*) {
    margin-inline-start: var(--webmodule-control-spacing-large);
  }

  .select--large.select--multiple .select__combobox {
    padding-inline-start: 0;
    padding-block: 4px;
  }

  .select--large .select__tags {
    gap: 4px;
  }

  /* Pills */
  .select--pill.select--small .select__combobox {
    border-radius: var(--webmodule-control-height-small);
  }

  .select--pill.select--medium .select__combobox {
    border-radius: var(--webmodule-control-height-medium);
  }

  .select--pill.select--large .select__combobox {
    border-radius: var(--webmodule-control-height-large);
  }

  /* Prefix and Suffix */
  .select__prefix,
  .select__suffix {
    flex: 0;
    display: inline-flex;
    align-items: center;
    color: var(--webmodule-control-placeholder-color);
  }

  .select__suffix::slotted(*) {
    margin-inline-start: var(--webmodule-control-spacing-small);
  }

  /* Clear button */
  .select__clear {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: inherit;
    color: var(--webmodule-control-icon-color);
    border: none;
    background: none;
    padding: 0;
    transition: var(--webmodule-animate-fast) color;
    cursor: pointer;
  }

  .select__clear:hover {
    color: var(--webmodule-control-icon-color-hover);
  }

  .select__clear:focus {
    outline: none;
  }

  /* Expand icon */
  .select__expand-icon {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    transition: var(--webmodule-animate-medium) rotate ease;
    rotate: 0;
    margin-inline-start: var(--webmodule-spacing-small);
  }

  .select--open .select__expand-icon {
    rotate: -180deg;
  }

  /* Listbox */
  .select__listbox {
    display: block;
    position: relative;
    font-family: var(--webmodule-font-sans);
    font-size: var(--webmodule-font-size-medium);
    font-weight: var(--webmodule-font-weight-normal);
    box-shadow: var(--webmodule-shadow-large);
    background: var(--webmodule-panel-background-color);
    border: solid var(--webmodule-panel-border-width) var(--webmodule-panel-border-color);
    border-radius: var(--webmodule-border-radius-medium);
    padding-block: var(--webmodule-spacing-x-small);
    padding-inline: 0;
    overflow: auto;
    overscroll-behavior: none;

    /* Comes from POPUP */
    max-width: var(--auto-size-available-width);
    max-height: var(--auto-size-available-height);
  }

  .select__listbox ::slotted(webmodule-divider) {
    --spacing: var(--webmodule-spacing-x-small);
  }

  .select__listbox ::slotted(small) {
    display: block;
    font-size: var(--webmodule-font-size-small);
    font-weight: var(--webmodule-font-weight-semibold);
    color: var(--webmodule-color-neutral-500);
    padding-block: var(--webmodule-spacing-x-small);
    padding-inline: var(--webmodule-spacing-x-large);
  }
`;
